// The exported code uses Tailwind CSS. Install Tailwind CSS in your dev environment to ensure all styles work.
import React, { useState } from 'react';
import * as echarts from 'echarts';
const App = () => {
    const [activeTab, setActiveTab] = useState('home');
    const [menuOpen, setMenuOpen] = useState(false);
    const [portfolioFilter, setPortfolioFilter] = useState('all');
    const [selectedCaseStudy, setSelectedCaseStudy] = useState(null);
    const [contactSubmitted, setContactSubmitted] = useState(false);
    const [newsletterSubmitted, setNewsletterSubmitted] = useState(false);

    const portfolioItems = [
        {
            id: 'haven-crest',
            title: 'Haven Crest Insurance Management System',
            category: 'Insurance Systems',
            categoryTag: 'InsurTech',
            duration: 'Enterprise Deployed',
            image: 'https://i.pinimg.com/736x/ad/b4/75/adb475287532a76a42b33eb8d38943c6.jpg',
            summary: 'Comprehensive insurance underwriting and policy lifecycle platform featuring automated dynamic ratings, broker commissions, IRA compliance, and digital claims vault.',
            description: 'Engineered and deployed for Haven Crest Insurance, this end-to-end platform digitizes the entire insurance policy lifecycle—from dynamic rating and underwriting to premium collections and claims processing, fully aligned with Insurance Regulatory Authority (IRA) standards.',
            highlights: [
                'Automated policy underwriting and dynamic actuarial rating matrix',
                'Intermediary and broker commission settlement engine with multi-tier tracking',
                'Customer self-service portal with end-to-end claims status tracking',
                'Secure document vault with automated IRA statutory reporting'
            ],
            impact: 'Reduced policy issuance turnaround time to under 4 minutes with 100% IRA statutory filing compliance.'
        },
        {
            id: 'prime-insurance',
            title: 'Prime Insurance Core Platform',
            category: 'Insurance Systems',
            categoryTag: 'Core Insurance',
            duration: 'Core Enterprise',
            image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80',
            summary: 'Enterprise multi-branch core insurance system with policy lifecycle automation, anti-fraud claims adjudication, and reinsurance treaty management.',
            description: 'Built for Prime Insurance, this core platform unifies multi-branch operations across general and medical lines, providing automated underwriting, risk distribution, claims adjudication with fraud checks, and synchronized general ledgers.',
            highlights: [
                'Multi-branch general and life insurance policy issuance and endorsement workflows',
                'Automated claims adjudication with intelligent fraud pattern detection',
                'Reinsurance treaty management (proportional and excess of loss risk distribution)',
                'Full general ledger integration and automated regulatory reporting'
            ],
            impact: 'Accelerated claims settlement by 70% while detecting and flagging over $250k in fraudulent claims.'
        },
        {
            id: 'bwallet-paygate',
            title: 'Bwallet Paygate System',
            category: 'FinTech & Payments',
            categoryTag: 'Payment Gateway',
            duration: 'High-Throughput Production',
            image: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?auto=format&fit=crop&w=800&q=80',
            summary: 'Omnichannel payment gateway aggregator integrating M-Pesa STK push, Airtel Money, card processing (Visa/Mastercard), and PesaLink with instant settlements.',
            description: 'Bwallet Paygate is BaruchSoft’s flagship payment aggregation gateway. Engineered for high transaction throughput, it bridges mobile money rails with card networks and core banking systems with bank-grade encryption and sub-second settlement speeds.',
            highlights: [
                'Real-time Safaricom M-Pesa Daraja 3.0 STK Push & C2B/B2C automated payouts',
                'Unified payment aggregation supporting Airtel Money, Visa, Mastercard, and PesaLink',
                'Multi-tenant merchant portal with live transaction analytics and automated webhook notifications',
                'Sub-second transaction settlement with auto-reconciliation and chargeback protection'
            ],
            impact: 'Processes 150,000+ daily transactions with 99.98% uptime and automated merchant reconciliations.'
        },
        {
            id: 'officio-leave',
            title: 'Officio - Leave Management System',
            category: 'Enterprise & HR',
            categoryTag: 'Enterprise HR',
            duration: 'Active SaaS',
            image: 'https://i.pinimg.com/736x/5a/7b/0b/5a7b0ba3fc73ee370822320de58467b8.jpg',
            summary: 'Intelligent human capital and leave management platform with multi-tier approval hierarchies, statutory leave calculations, calendar sync, and HR audit reporting.',
            description: 'Officio simplifies workforce scheduling and leave governance for medium-to-large organizations. It automates leave balance accruals, escalates approval workflows across multi-tiered management, and ensures compliance with national labor laws.',
            highlights: [
                'Configurable statutory and company leave categories (Annual, Sick, Maternity, Compassionate)',
                'Multi-level department approval workflows with real-time email and mobile notifications',
                'Team availability heatmaps and calendar synchronization with Google Workspace and Microsoft Outlook',
                'Automated leave accrual engines, encashment calculators, and comprehensive HR payroll audit trails'
            ],
            impact: 'Eliminated manual leave administration, reduced HR processing overhead by 85%, and gave teams 100% staffing visibility.'
        },
        {
            id: 'symu-finance',
            title: 'Symu Finance Management System',
            category: 'FinTech & Payments',
            categoryTag: 'Corporate Finance',
            duration: 'Enterprise Deployed',
            image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80',
            summary: 'Comprehensive corporate financial management and accounting platform featuring double-entry general ledger, automated bank reconciliation, and cash flow forecasting.',
            description: 'Symu Finance Management empowers corporate finance teams with automated ledger accounting, multi-currency cash management, automated invoice dispatch, and financial forecasting dashboards built on modern microservices.',
            highlights: [
                'Real-time double-entry accounting ledger with multi-currency chart of accounts',
                'Automated bank feed and statement reconciliation with discrepancy detection',
                'Cash flow forecasting models, budget variance analytics, and expense approval routing',
                'Full invoice lifecycle management with recurring billing and automated statutory VAT computation'
            ],
            impact: 'Accelerated month-end financial book closures from 12 days to 24 hours with complete financial audit compliance.'
        },
        {
            id: 'uhai-pos',
            title: 'UHAI POS',
            category: 'Healthcare & Retail',
            categoryTag: 'Retail POS',
            duration: 'Retail Production',
            image: 'https://readdy.ai/api/search-image?query=Modern%20fintech%20application%20interface%20displayed%20on%20premium%20devices%20showing%20elegant%20dashboard%20with%20financial%20data%20visualization.%20Professional%20office%20setting%20with%20subtle%20gold%20accents%20against%20a%20neutral%20sophisticated%20background&width=600&height=500&seq=16&orientation=landscape',
            summary: 'Digital transformation of retail POS and inventory management resulting in 60% improved operational efficiency and multi-branch synchronization.',
            description: 'UHAI POS is a distributed cloud point-of-sale system that guarantees uninterrupted sales transactions with offline-first synchronization, dynamic inventory management, barcode scanning, and multi-till fiscal reporting.',
            highlights: [
                'Offline-first cashier mode with seamless automatic background cloud sync',
                'Multi-branch stock transfer, low-stock threshold alerts, and purchase orders',
                'Integrated mobile money and receipt printing support',
                'Granular sales analytics and cashier shift reconciliation'
            ],
            impact: 'Over 60% improvement in checkout speeds and zero loss of sales during network outages.'
        },
        {
            id: 'villagecan',
            title: 'VillageCAN',
            category: 'Healthcare & Retail',
            categoryTag: 'Healthcare',
            duration: 'Healthcare Suite',
            image: 'https://readdy.ai/api/search-image?query=Healthcare%20management%20system%20interface%20showing%20patient%20data%20and%20medical%20analytics%20dashboard.%20Professional%20medical%20setting%20with%20modern%20design%20elements%20and%20subtle%20gold%20accents%20against%20a%20neutral%20sophisticated%20background&width=600&height=500&seq=17&orientation=landscape',
            summary: 'Healthcare management and patient record system reducing patient wait times by 45% with digital clinical records and pharmacy dispensing.',
            description: 'Comprehensive digital healthcare management system connecting outpatient registration, electronic health records (EHR), physician triage, laboratory orders, and pharmacy inventory.',
            highlights: [
                'Digital patient queue management and electronic health records',
                'Physician diagnosis, digital prescription, and automated lab request routing',
                'Integrated pharmacy inventory tracking and dispensing verification',
                'Insurance billing reconciliation and NHIF/SHIF integration ready'
            ],
            impact: 'Cut average clinic patient waiting times by 45% and eliminated prescription fulfillment errors.'
        }
    ];

    const filterCategories = [
        { key: 'all', label: 'All Projects' },
        { key: 'Insurance Systems', label: 'Insurance Systems' },
        { key: 'FinTech & Payments', label: 'FinTech & Payments' },
        { key: 'Enterprise & HR', label: 'Enterprise & HR' },
        { key: 'Healthcare & Retail', label: 'Healthcare & Retail' }
    ];

    const filteredPortfolio = portfolioFilter === 'all'
        ? portfolioItems
        : portfolioItems.filter(item => item.category === portfolioFilter);

    const scrollToSection = (sectionId) => {
        const section = document.getElementById(sectionId);
        if (section) {
            section.scrollIntoView({ behavior: 'smooth' });
            setActiveTab(sectionId);
        }
    };

    React.useEffect(() => {
        // Initialize charts
        let clientGrowthChart = null;
        let projectSuccessChart = null;
        try {
            const clientGrowthEl = document.getElementById('clientGrowthChart');
            const projectSuccessEl = document.getElementById('projectSuccessChart');
            if (clientGrowthEl) {
                clientGrowthChart = echarts.init(clientGrowthEl);
            }
            if (projectSuccessEl) {
                projectSuccessChart = echarts.init(projectSuccessEl);
            }
        } catch (err) {
            console.warn('ECharts init notice:', err);
        }
        const clientGrowthOption = {
            animation: false,
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow'
                }
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: ['2020', '2021', '2022', '2023', '2024', '2025'],
                axisLine: {
                    lineStyle: {
                        color: '#ccc'
                    }
                }
            },
            yAxis: {
                type: 'value',
                axisLine: {
                    lineStyle: {
                        color: '#ccc'
                    }
                }
            },
            series: [
                {
                    name: 'Clients',
                    type: 'bar',
                    data: [30, 52, 78, 110, 145, 182],
                    itemStyle: {
                        color: '#9f7a2a'
                    }
                }
            ]
        };
        const projectSuccessOption = {
            animation: false,
            tooltip: {
                trigger: 'item'
            },
            legend: {
                top: '5%',
                left: 'center',
                textStyle: {
                    color: '#333'
                }
            },
            series: [
                {
                    name: 'Project Status',
                    type: 'pie',
                    radius: ['40%', '70%'],
                    avoidLabelOverlap: false,
                    itemStyle: {
                        borderRadius: 10,
                        borderColor: '#fff',
                        borderWidth: 2
                    },
                    label: {
                        show: false,
                        position: 'center'
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: 20,
                            fontWeight: 'bold'
                        }
                    },
                    labelLine: {
                        show: false
                    },
                    data: [
                        { value: 92, name: 'Completed', itemStyle: { color: '#9f7a2a' } },
                        { value: 5, name: 'In Progress', itemStyle: { color: '#d4af37' } },
                        { value: 3, name: 'Planning', itemStyle: { color: '#e6d5a8' } }
                    ]
                }
            ]
        };
        try {
            if (clientGrowthChart) clientGrowthChart.setOption(clientGrowthOption);
            if (projectSuccessChart) projectSuccessChart.setOption(projectSuccessOption);
        } catch (err) {
            console.warn('ECharts setOption notice:', err);
        }
        const handleResize = () => {
            if (clientGrowthChart) clientGrowthChart.resize();
            if (projectSuccessChart) projectSuccessChart.resize();
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            try {
                if (clientGrowthChart) clientGrowthChart.dispose();
                if (projectSuccessChart) projectSuccessChart.dispose();
            } catch (err) {}
        };
    }, []);
    return (
        <div className="min-h-screen bg-gray-900 text-gray-100 font-sans">
            {/* Header */}
            <header className="sticky top-0 z-50 bg-gray-950/85 backdrop-blur-md border-b border-gray-800/60 shadow-lg">
                <div className="container mx-auto px-6 py-4">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center">
                            <img src="/logo.png" alt="BaruchSoft Logo" className="h-14 sm:h-16 md:h-[72px] w-auto object-contain cursor-pointer rounded-xl border border-amber-500/30 hover:border-amber-400/60 p-1 bg-white/5 transition-all hover:scale-105" onClick={() => scrollToSection('home')} />
                        </div>
                        {/* Desktop Navigation */}
                        <nav className="hidden md:flex space-x-8">
                            <button
                                onClick={() => scrollToSection('home')}
                                className={`${activeTab === 'home' ? 'text-amber-500 border-b-2 border-amber-500' : 'text-gray-300 hover:text-amber-500'} !rounded-button whitespace-nowrap cursor-pointer px-2 py-1 font-medium transition-all`}
                            >
                                Home
                            </button>
                            <button
                                onClick={() => scrollToSection('services')}
                                className={`${activeTab === 'services' ? 'text-amber-500 border-b-2 border-amber-500' : 'text-gray-300 hover:text-amber-500'} !rounded-button whitespace-nowrap cursor-pointer px-2 py-1 font-medium transition-all`}
                            >
                                Services
                            </button>
                            <button
                                onClick={() => scrollToSection('portfolio')}
                                className={`${activeTab === 'portfolio' ? 'text-amber-500 border-b-2 border-amber-500' : 'text-gray-300 hover:text-amber-500'} !rounded-button whitespace-nowrap cursor-pointer px-2 py-1 font-medium transition-all`}
                            >
                                Portfolio
                            </button>
                            <button
                                onClick={() => scrollToSection('team')}
                                className={`${activeTab === 'team' ? 'text-amber-500 border-b-2 border-amber-500' : 'text-gray-300 hover:text-amber-500'} !rounded-button whitespace-nowrap cursor-pointer px-2 py-1 font-medium transition-all`}
                            >
                                Our Team
                            </button>
                            <button
                                onClick={() => scrollToSection('contact')}
                                className={`${activeTab === 'contact' ? 'text-amber-500 border-b-2 border-amber-500' : 'text-gray-300 hover:text-amber-500'} !rounded-button whitespace-nowrap cursor-pointer px-2 py-1 font-medium transition-all`}
                            >
                                Contact
                            </button>
                        </nav>
                        {/* Mobile menu button */}
                        <div className="md:hidden">
                            <button
                                onClick={() => setMenuOpen(!menuOpen)}
                                className="text-gray-300 hover:text-amber-500 focus:outline-none !rounded-button whitespace-nowrap cursor-pointer p-2"
                                aria-label="Toggle menu"
                            >
                                <i className={`fas ${menuOpen ? 'fa-times' : 'fa-bars'} text-2xl`}></i>
                            </button>
                        </div>
                    </div>
                    {/* Mobile Navigation */}
                    <div className={`fixed inset-0 bg-gray-950 bg-opacity-70 z-50 transition-opacity duration-300 ${menuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                        onClick={() => setMenuOpen(false)}>
                        <div className={`fixed top-0 right-0 h-full w-4/5 max-w-sm bg-gray-900 border-l border-gray-800 shadow-xl transform transition-transform duration-300 ease-in-out ${menuOpen ? 'translate-x-0' : 'translate-x-full'}`}
                            onClick={(e) => e.stopPropagation()}>
                            <div className="flex justify-between items-center p-4 border-b border-gray-800">
                                <img src="/logo.png" alt="BaruchSoft Logo" className="h-12 w-auto object-contain rounded-xl border border-amber-500/30 p-1 bg-white/5" />
                                <button
                                    onClick={() => setMenuOpen(false)}
                                    className="text-gray-400 hover:text-amber-500 focus:outline-none p-2"
                                    aria-label="Close menu"
                                >
                                    <i className="fas fa-times text-2xl"></i>
                                </button>
                            </div>
                            <nav className="p-4">
                                <div className="flex flex-col space-y-4">
                                    <button
                                        onClick={() => {
                                            scrollToSection('home');
                                            setMenuOpen(false);
                                        }}
                                        className={`${activeTab === 'home' ? 'text-amber-500 bg-gray-800' : 'text-gray-300 hover:bg-gray-850'} !rounded-button whitespace-nowrap cursor-pointer px-4 py-3 rounded-md text-lg flex items-center`}
                                    >
                                        <i className="fas fa-home mr-3"></i> Home
                                    </button>
                                    <button
                                        onClick={() => {
                                            scrollToSection('services');
                                            setMenuOpen(false);
                                        }}
                                        className={`${activeTab === 'services' ? 'text-amber-500 bg-gray-800' : 'text-gray-300 hover:bg-gray-850'} !rounded-button whitespace-nowrap cursor-pointer px-4 py-3 rounded-md text-lg flex items-center`}
                                    >
                                        <i className="fas fa-cogs mr-3"></i> Services
                                    </button>
                                    <button
                                        onClick={() => {
                                            scrollToSection('portfolio');
                                            setMenuOpen(false);
                                        }}
                                        className={`${activeTab === 'portfolio' ? 'text-amber-500 bg-gray-800' : 'text-gray-300 hover:bg-gray-850'} !rounded-button whitespace-nowrap cursor-pointer px-4 py-3 rounded-md text-lg flex items-center`}
                                    >
                                        <i className="fas fa-briefcase mr-3"></i> Portfolio
                                    </button>
                                    <button
                                        onClick={() => {
                                            scrollToSection('team');
                                            setMenuOpen(false);
                                        }}
                                        className={`${activeTab === 'team' ? 'text-amber-500 bg-gray-800' : 'text-gray-300 hover:bg-gray-850'} !rounded-button whitespace-nowrap cursor-pointer px-4 py-3 rounded-md text-lg flex items-center`}
                                    >
                                        <i className="fas fa-users mr-3"></i> Our Team
                                    </button>
                                    <button
                                        onClick={() => {
                                            scrollToSection('contact');
                                            setMenuOpen(false);
                                        }}
                                        className={`${activeTab === 'contact' ? 'text-amber-500 bg-gray-800' : 'text-gray-300 hover:bg-gray-850'} !rounded-button whitespace-nowrap cursor-pointer px-4 py-3 rounded-md text-lg flex items-center`}
                                    >
                                        <i className="fas fa-envelope mr-3"></i> Contact
                                    </button>
                                </div>
                            </nav>
                        </div>
                    </div>
                </div>
            </header>
            {/* Hero Section */}
            <section id="home" className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-gray-950 isolate">
                {/* Background Image */}
                <img
                    src="https://i.pinimg.com/736x/82/c3/2d/82c32de9c1e43b9d95e4a5680a1c0284.jpg"
                    alt=""
                    aria-hidden="true"
                    loading="eager"
                    className="absolute inset-0 w-full h-full object-cover object-center z-0 motion-safe:animate-slow-zoom"
                />

                {/* Cinematic gradient overlays — kept light so the image breathes through */}
                <div className="absolute inset-0 bg-gradient-to-b from-gray-950/55 via-gray-950/20 to-gray-950/85 z-[1]"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-gray-950/40 via-transparent to-gray-950/40 z-[1]"></div>

                {/* Radial vignette — soft */}
                <div className="absolute inset-0 hero-vignette z-[2]"></div>

                {/* Tech grid overlay */}
                <div className="absolute inset-0 tech-grid opacity-[0.08] z-[3]"></div>

                {/* Subtle film grain */}
                <div className="absolute inset-0 grain-overlay z-[4] pointer-events-none"></div>

                {/* Glowing accent orbs */}
                <div className="absolute top-1/3 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] sm:w-[640px] sm:h-[640px] bg-amber-500/20 rounded-full blur-[140px] animate-pulse-glow z-[3]"></div>
                <div className="absolute bottom-1/4 right-1/4 translate-x-1/2 translate-y-1/2 w-[340px] h-[340px] sm:w-[520px] sm:h-[520px] bg-orange-600/15 rounded-full blur-[120px] animate-pulse-glow z-[3]" style={{ animationDelay: '2.5s' }}></div>

                {/* Decorative side accent lines */}
                <div className="hidden md:block absolute top-1/2 left-6 -translate-y-1/2 w-px h-32 bg-gradient-to-b from-transparent via-amber-500/50 to-transparent z-[5]"></div>
                <div className="hidden md:block absolute top-1/2 right-6 -translate-y-1/2 w-px h-32 bg-gradient-to-b from-transparent via-amber-500/50 to-transparent z-[5]"></div>

                {/* Hero Content */}
                <div className="container mx-auto px-6 relative z-10 w-full py-28 md:py-36 lg:py-40">
                    <div className="max-w-5xl mx-auto flex flex-col items-center text-center">

                        {/* Accent Badge */}
                        <div className="inline-flex items-center space-x-2.5 bg-amber-500/10 backdrop-blur-md border border-amber-500/30 rounded-full px-5 py-2 mb-8 transition-all hover:border-amber-400/60 hover:bg-amber-500/15 hover:scale-105 animate-hero-rise hero-rise-1">
                            <span className="flex h-2 w-2 relative">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-400"></span>
                            </span>
                            <span className="text-[11px] sm:text-xs font-semibold text-amber-300 uppercase tracking-[0.22em]">Excellence in Digital Transformation</span>
                        </div>

                        {/* Headline */}
                        <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] xl:text-[6.5rem] font-extrabold text-white mb-8 leading-[1.02] font-heading tracking-tight animate-hero-rise hero-rise-2">
                            Elevating Businesses<br className="hidden sm:block" />
                            <span className="sm:hidden"> </span>Through{' '}
                            <span className="relative inline-block">
                                <span className="bg-gradient-to-r from-amber-300 via-orange-400 to-amber-500 bg-clip-text text-transparent bg-[length:200%_auto] animate-text-shimmer">
                                    Innovative
                                </span>
                            </span>{' '}
                            <span className="bg-gradient-to-r from-amber-300 via-orange-400 to-amber-500 bg-clip-text text-transparent">
                                Technology
                            </span>
                        </h1>

                        {/* Subheadline */}
                        <p className="text-lg md:text-xl lg:text-2xl text-gray-300 mb-12 max-w-3xl leading-relaxed font-light animate-hero-rise hero-rise-3">
                            BaruchSoft delivers <span className="text-white font-medium">premium software development</span> and IT consulting tailored to transform your operations and drive{' '}
                            <span className="text-amber-400 font-medium">exceptional corporate results</span>.
                        </p>

                        {/* CTA Row */}
                        <div className="flex flex-col sm:flex-row gap-4 mb-14 animate-hero-rise hero-rise-4">
                            <button
                                onClick={() => scrollToSection('services')}
                                className="group relative bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-orange-500 text-white px-9 py-[1.05rem] rounded-xl shadow-2xl shadow-amber-900/40 hover:shadow-amber-500/40 transform hover:-translate-y-1 transition-all duration-300 font-semibold !rounded-button whitespace-nowrap cursor-pointer flex justify-center items-center text-base overflow-hidden"
                            >
                                <span className="absolute inset-0 bg-white/15 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out"></span>
                                <span className="relative z-10">Explore Our Services</span>
                                <i className="fas fa-arrow-right ml-3 transition-transform group-hover:translate-x-1.5 relative z-10"></i>
                            </button>
                            <button
                                onClick={() => scrollToSection('contact')}
                                className="group bg-white/5 hover:bg-white/[0.09] backdrop-blur-md border border-white/20 hover:border-amber-400/50 text-white px-9 py-[1.05rem] rounded-xl transform hover:-translate-y-1 transition-all duration-300 font-semibold !rounded-button whitespace-nowrap cursor-pointer flex justify-center items-center text-base"
                            >
                                <i className="fas fa-calendar-alt mr-3 text-amber-400 group-hover:rotate-12 transition-transform duration-300"></i>
                                <span>Book a Consultation</span>
                            </button>
                        </div>

                        {/* Capability Pills — what we deliver */}
                        <div className="flex flex-wrap items-center justify-center gap-2.5 sm:gap-3 mb-14 animate-hero-rise hero-rise-5">
                            {[
                                { icon: 'fa-code', label: 'Custom Software' },
                                { icon: 'fa-cloud', label: 'Cloud Solutions' },
                                { icon: 'fa-microchip', label: 'AI & Automation' },
                                { icon: 'fa-shield-halved', label: 'Cybersecurity' },
                                { icon: 'fa-lightbulb', label: 'IT Consulting' },
                            ].map((pill) => (
                                <span
                                    key={pill.label}
                                    className="inline-flex items-center space-x-2 bg-white/[0.04] backdrop-blur-sm border border-white/10 hover:border-amber-400/40 hover:bg-white/[0.07] rounded-full px-4 py-2 text-xs sm:text-sm text-gray-300 hover:text-white transition-all cursor-default"
                                >
                                    <i className={`fas ${pill.icon} text-amber-400 text-[11px]`}></i>
                                    <span className="font-medium tracking-wide">{pill.label}</span>
                                </span>
                            ))}
                        </div>

                        {/* Trust Signals */}
                        <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8 border-t border-white/10 pt-10 animate-hero-rise hero-rise-6">
                            <div className="flex items-center space-x-4">
                                <div className="flex -space-x-3">
                                    <img className="inline-block h-11 w-11 rounded-full ring-2 ring-gray-950 object-cover object-top" src="/isaacomollo.jpg" alt="Isaac Omollo" />
                                    <img className="inline-block h-11 w-11 rounded-full ring-2 ring-gray-950 object-cover object-top" src="/azronbrian.png" alt="Azron Brian" />
                                    <img className="inline-block h-11 w-11 rounded-full ring-2 ring-gray-950 object-cover object-top" src="/hezbornshikuku.jpeg" alt="Hezborn Achido" />
                                    <img className="inline-block h-11 w-11 rounded-full ring-2 ring-gray-950 object-cover object-top" src="/victormaina.jpeg" alt="Victor Maina" />
                                </div>
                                <div className="text-left">
                                    <div className="flex items-center text-amber-400 text-sm">
                                        <i className="fas fa-star mr-0.5"></i>
                                        <i className="fas fa-star mr-0.5"></i>
                                        <i className="fas fa-star mr-0.5"></i>
                                        <i className="fas fa-star mr-0.5"></i>
                                        <i className="fas fa-star mr-0.5"></i>
                                        <span className="text-white font-bold ml-2">5.0</span>
                                    </div>
                                    <p className="text-xs text-gray-400">Trusted by 180+ global businesses</p>
                                </div>
                            </div>

                            <div className="hidden sm:block h-12 w-px bg-white/10"></div>

                            <div className="flex items-center space-x-3">
                                <div className="h-11 w-11 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                                    <i className="fas fa-shield-halved text-amber-400 text-lg"></i>
                                </div>
                                <div className="text-left">
                                    <p className="text-white text-sm font-bold">ISO 27001 Aligned</p>
                                    <p className="text-xs text-gray-400">Enterprise-grade security</p>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                {/* Scroll Indicator */}
                <button
                    onClick={() => scrollToSection('services')}
                    aria-label="Scroll to services"
                    className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center group cursor-pointer"
                >
                    <span className="text-[10px] uppercase tracking-[0.3em] text-gray-400 group-hover:text-amber-400 transition-colors mb-3 font-semibold">Scroll</span>
                    <div className="w-6 h-10 rounded-full border-2 border-white/30 group-hover:border-amber-400/70 flex justify-center pt-2 transition-colors">
                        <span className="w-1 h-2 bg-amber-400 rounded-full animate-scroll-cue"></span>
                    </div>
                </button>
            </section>
            {/* Stats Section */}
            <section className="py-12 sm:py-16 bg-white">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
                        <div className="text-center p-4 sm:p-6 rounded-lg shadow-md bg-gradient-to-br from-amber-50 to-white transform transition-transform duration-300 hover:scale-105">
                            <div className="text-amber-700 mb-2">
                                <i className="fas fa-project-diagram text-2xl sm:text-3xl"></i>
                            </div>
                            <h3 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-1 sm:mb-2">250+</h3>
                            <p className="text-gray-600 text-sm sm:text-base">Projects Completed</p>
                        </div>
                        <div className="text-center p-4 sm:p-6 rounded-lg shadow-md bg-gradient-to-br from-amber-50 to-white transform transition-transform duration-300 hover:scale-105">
                            <div className="text-amber-700 mb-2">
                                <i className="fas fa-users text-2xl sm:text-3xl"></i>
                            </div>
                            <h3 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-1 sm:mb-2">180+</h3>
                            <p className="text-gray-600 text-sm sm:text-base">Satisfied Clients</p>
                        </div>
                        <div className="text-center p-4 sm:p-6 rounded-lg shadow-md bg-gradient-to-br from-amber-50 to-white transform transition-transform duration-300 hover:scale-105">
                            <div className="text-amber-700 mb-2">
                                <i className="fas fa-code-branch text-2xl sm:text-3xl"></i>
                            </div>
                            <h3 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-1 sm:mb-2">15+</h3>
                            <p className="text-gray-600 text-sm sm:text-base">Years of Experience</p>
                        </div>
                        <div className="text-center p-4 sm:p-6 rounded-lg shadow-md bg-gradient-to-br from-amber-50 to-white transform transition-transform duration-300 hover:scale-105">
                            <div className="text-amber-700 mb-2">
                                <i className="fas fa-award text-2xl sm:text-3xl"></i>
                            </div>
                            <h3 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-1 sm:mb-2">98%</h3>
                            <p className="text-gray-600 text-sm sm:text-base">Client Retention</p>
                        </div>
                    </div>
                </div>
            </section>
            {/* Services Section */}
            <section id="services" className="py-12 sm:py-16 md:py-20 bg-gray-50">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="text-center mb-10 sm:mb-16">
                        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">Our Premium Services</h2>
                        <div className="w-16 sm:w-24 h-1 bg-amber-600 mx-auto mb-4 sm:mb-6"></div>
                        <p className="text-gray-600 max-w-3xl mx-auto text-sm sm:text-base px-2">
                            Discover our comprehensive range of technology solutions designed to elevate your business operations and drive exceptional growth.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:shadow-xl hover:-translate-y-2 flex flex-col justify-between">
                            <div className="h-40 sm:h-48 overflow-hidden">
                                <img
                                    src="https://i.pinimg.com/736x/c8/86/35/c88635788a2f0e4bc4073656742c00f6.jpg"
                                    alt="Project Management"
                                    className="w-full h-full object-cover object-top"
                                    loading="lazy"
                                />
                            </div>
                            <div className="p-4 sm:p-6 flex-1 flex flex-col justify-between">
                                <div>
                                    <div className="text-amber-700 mb-2 flex items-center">
                                        <i className="fas fa-tasks text-lg sm:text-xl"></i>
                                        <h3 className="text-lg sm:text-xl font-bold text-gray-800 ml-2">Project Management Solutions</h3>
                                    </div>
                                    <p className="text-gray-600 mb-4 text-sm sm:text-base">
                                        Streamline your workflow with our comprehensive project management platform designed for maximum efficiency and transparency.
                                    </p>
                                </div>
                                <button
                                    onClick={() => setSelectedCaseStudy({
                                        title: 'Project Management Solutions',
                                        category: 'Enterprise Productivity',
                                        categoryTag: 'Productivity',
                                        duration: 'Enterprise Suite',
                                        image: 'https://i.pinimg.com/736x/c8/86/35/c88635788a2f0e4bc4073656742c00f6.jpg',
                                        description: 'An enterprise-wide collaboration and project management ecosystem providing real-time milestones, resource allocation dashboards, sprint analytics, and executive progress reports.',
                                        highlights: [
                                            'Agile sprint boards and Gantt roadmap tracking',
                                            'Automated task assignments and SLA escalation alerts',
                                            'Resource utilization and capacity forecasting',
                                            'Integrated document sharing and role-based access'
                                        ],
                                        impact: 'Improved cross-functional delivery predictability by 55% and reduced project delays.'
                                    })}
                                    className="text-amber-700 hover:text-amber-800 font-medium flex items-center !rounded-button whitespace-nowrap cursor-pointer text-sm sm:text-base mt-2"
                                >
                                    Learn More <i className="fas fa-arrow-right ml-2"></i>
                                </button>
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:shadow-xl hover:-translate-y-2 flex flex-col justify-between">
                            <div className="h-40 sm:h-48 overflow-hidden">
                                <img
                                    src="https://i.pinimg.com/736x/0b/4c/84/0b4c84fff48a3ad3b3172325de05c2c5.jpg"
                                    alt="Custom Software Development"
                                    className="w-full h-full object-cover object-top"
                                    loading="lazy"
                                />
                            </div>
                            <div className="p-4 sm:p-6 flex-1 flex flex-col justify-between">
                                <div>
                                    <div className="text-amber-700 mb-2 flex items-center">
                                        <i className="fas fa-code text-lg sm:text-xl"></i>
                                        <h3 className="text-lg sm:text-xl font-bold text-gray-800 ml-2">Custom Software Development</h3>
                                    </div>
                                    <p className="text-gray-600 mb-4 text-sm sm:text-base">
                                        Tailored software solutions designed to address your unique business challenges and optimize operational efficiency.
                                    </p>
                                </div>
                                <button
                                    onClick={() => setSelectedCaseStudy({
                                        title: 'Custom Software Development',
                                        category: 'Engineering Services',
                                        categoryTag: 'Custom Dev',
                                        duration: 'Bespoke Engineering',
                                        image: 'https://i.pinimg.com/736x/0b/4c/84/0b4c84fff48a3ad3b3172325de05c2c5.jpg',
                                        description: 'High-performance bespoke engineering covering full-stack cloud web apps, reactive native mobile solutions, and resilient distributed backend microservices.',
                                        highlights: [
                                            'Scalable cloud-native architecture using modern frameworks',
                                            'Secure RESTful & GraphQL microservices with automated CI/CD pipelines',
                                            'Strict security auditing, automated unit tests, and performance benchmarks',
                                            'Continuous maintenance and enterprise SLA support'
                                        ],
                                        impact: 'Engineered robust digital products that scaled effortlessly to hundreds of thousands of users.'
                                    })}
                                    className="text-amber-700 hover:text-amber-800 font-medium flex items-center !rounded-button whitespace-nowrap cursor-pointer text-sm sm:text-base mt-2"
                                >
                                    Learn More <i className="fas fa-arrow-right ml-2"></i>
                                </button>
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:shadow-xl hover:-translate-y-2 flex flex-col justify-between">
                            <div className="h-40 sm:h-48 overflow-hidden">
                                <img
                                    src="https://i.pinimg.com/736x/c2/bd/78/c2bd78ee39e885e7be5a4c1b3ff5d59f.jpg"
                                    alt="IT Consulting Services"
                                    className="w-full h-full object-cover object-top"
                                    loading="lazy"
                                />
                            </div>
                            <div className="p-4 sm:p-6 flex-1 flex flex-col justify-between">
                                <div>
                                    <div className="text-amber-700 mb-2 flex items-center">
                                        <i className="fas fa-lightbulb text-lg sm:text-xl"></i>
                                        <h3 className="text-lg sm:text-xl font-bold text-gray-800 ml-2">IT Consulting Services</h3>
                                    </div>
                                    <p className="text-gray-600 mb-4 text-sm sm:text-base">
                                        Strategic technology guidance to help you make informed decisions and implement effective IT solutions for your business.
                                    </p>
                                </div>
                                <button
                                    onClick={() => setSelectedCaseStudy({
                                        title: 'IT Consulting Services',
                                        category: 'Strategic Advisory',
                                        categoryTag: 'Consulting',
                                        duration: 'Advisory & Transformation',
                                        image: 'https://i.pinimg.com/736x/c2/bd/78/c2bd78ee39e885e7be5a4c1b3ff5d59f.jpg',
                                        description: 'Executive digital transformation advisory, cybersecurity posture assessments, legacy modernization roadmaps, and cloud migration frameworks.',
                                        highlights: [
                                            'Comprehensive enterprise IT readiness and architectural gap assessments',
                                            'Cloud migration strategies for AWS, GCP, and hybrid on-prem environments',
                                            'Cybersecurity defense, penetration testing, and compliance frameworks',
                                            'Cost optimization, devops automation, and operational SLA tuning'
                                        ],
                                        impact: 'Helped institutions reduce IT infrastructure expenditure by up to 35% while increasing system resilience.'
                                    })}
                                    className="text-amber-700 hover:text-amber-800 font-medium flex items-center !rounded-button whitespace-nowrap cursor-pointer text-sm sm:text-base mt-2"
                                >
                                    Learn More <i className="fas fa-arrow-right ml-2"></i>
                                </button>
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:shadow-xl hover:-translate-y-2 flex flex-col justify-between">
                            <div className="h-40 sm:h-48 overflow-hidden relative">
                                <img
                                    src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=800&q=80"
                                    alt="WSO2 API Management & Integration Platform"
                                    className="w-full h-full object-cover object-top"
                                    loading="lazy"
                                />
                                <div className="absolute top-3 right-3 bg-amber-600 text-white text-xs font-semibold px-2.5 py-1 rounded-full shadow">
                                    Enterprise Grade
                                </div>
                            </div>
                            <div className="p-4 sm:p-6 flex-1 flex flex-col justify-between">
                                <div>
                                    <div className="text-amber-700 mb-2 flex items-center">
                                        <i className="fas fa-network-wired text-lg sm:text-xl"></i>
                                        <h3 className="text-lg sm:text-xl font-bold text-gray-800 ml-2">WSO2 API Management & Integration</h3>
                                    </div>
                                    <p className="text-gray-600 mb-4 text-sm sm:text-base">
                                        Enterprise API gateway, Micro Integrator ESB, zero-trust security, event-driven streaming, and seamless core banking & fintech interoperability.
                                    </p>
                                </div>
                                <button
                                    onClick={() => setSelectedCaseStudy({
                                        title: 'WSO2 API Management & Integration Platform',
                                        category: 'Enterprise Middleware',
                                        categoryTag: 'API & ESB',
                                        duration: 'Enterprise Architecture',
                                        image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1200&q=80',
                                        description: 'Full-lifecycle API management and enterprise service bus (ESB) architecture designed for high-availability financial institutions, telecommunications, and distributed core systems.',
                                        highlights: [
                                            'WSO2 API Manager 4.x deployment with high-throughput gateway clusters',
                                            'Micro Integrator (MI) ESB orchestration across legacy databases, SOAP, and modern REST APIs',
                                            'Zero-Trust security with OAuth2 token revocation, mTLS, and RBAC policy enforcement',
                                            'Core Banking and FinTech connectors for real-time transactional event streaming',
                                            'Choreo Cloud & hybrid on-premise governance with automated developer portals'
                                        ],
                                        impact: 'Delivers 99.99% gateway uptime, sub-50ms API routing, and streamlined third-party developer onboarding.'
                                    })}
                                    className="text-amber-700 hover:text-amber-800 font-medium flex items-center !rounded-button whitespace-nowrap cursor-pointer text-sm sm:text-base mt-2"
                                >
                                    Learn More <i className="fas fa-arrow-right ml-2"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="text-center mt-8 sm:mt-12">
                        <button className="bg-amber-700 hover:bg-amber-800 text-white px-6 sm:px-8 py-3 rounded-md shadow-md transition-colors font-medium !rounded-button whitespace-nowrap cursor-pointer flex items-center justify-center mx-auto">
                            <span>View All Services</span>
                            <i className="fas fa-chevron-right ml-2"></i>
                        </button>
                    </div>
                </div>
            </section>
            {/* Project Management Dashboard Preview */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <div className="flex flex-col md:flex-row items-center">
                        <div className="md:w-1/2 mb-10 md:mb-0 md:pr-12">
                            <h2 className="text-3xl font-bold text-gray-800 mb-6">Streamline Your Projects with Our Advanced Dashboard</h2>
                            <p className="text-gray-600 mb-6">
                                Our comprehensive project management platform provides real-time insights, collaborative tools, and intuitive interfaces to keep your team aligned and your projects on track.
                            </p>
                            <div className="space-y-4 mb-8">
                                <div className="flex items-start">
                                    <div className="text-amber-600 mr-4 mt-1">
                                        <i className="fas fa-chart-line text-xl"></i>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-800 mb-1">Real-time Analytics</h3>
                                        <p className="text-gray-600">Monitor project performance with advanced metrics and customizable reports.</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="text-amber-600 mr-4 mt-1">
                                        <i className="fas fa-users-cog text-xl"></i>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-800 mb-1">Team Collaboration</h3>
                                        <p className="text-gray-600">Seamless communication tools to keep your team connected and productive.</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="text-amber-600 mr-4 mt-1">
                                        <i className="fas fa-shield-alt text-xl"></i>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-800 mb-1">Enterprise Security</h3>
                                        <p className="text-gray-600">Bank-level encryption and security protocols to protect your sensitive data.</p>
                                    </div>
                                </div>
                            </div>
                            <button className="bg-amber-700 hover:bg-amber-800 text-white px-6 py-3 rounded-md shadow-md transition-colors font-medium !rounded-button whitespace-nowrap cursor-pointer">
                                Schedule a Demo
                            </button>
                        </div>
                        <div className="md:w-1/2">
                            <div className="rounded-lg shadow-2xl overflow-hidden">
                                <img
                                    src="https://readdy.ai/api/search-image?query=A%20premium%20project%20management%20dashboard%20interface%20displayed%20on%20multiple%20devices%20including%20desktop%20and%20tablet.%20The%20dashboard%20shows%20elegant%20charts%2C%20task%20management%2C%20team%20collaboration%20features%2C%20and%20project%20timelines%20with%20a%20luxury%20gold%20and%20dark%20color%20scheme.%20The%20interface%20is%20clean%2C%20modern%20and%20professional%20against%20a%20neutral%20background&width=700&height=500&seq=5&orientation=landscape"
                                    alt="Project Management Dashboard"
                                    className="w-full h-auto"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            {/* Analytics Section */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Driving Success Through Data</h2>
                        <div className="w-24 h-1 bg-amber-600 mx-auto mb-6"></div>
                        <p className="text-gray-600 max-w-3xl mx-auto">
                            Our solutions are backed by powerful analytics that provide actionable insights to optimize your business operations.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="bg-white rounded-lg shadow-lg p-6">
                            <h3 className="text-xl font-bold text-gray-800 mb-4">Client Growth</h3>
                            <div id="clientGrowthChart" className="w-full h-80"></div>
                        </div>
                        <div className="bg-white rounded-lg shadow-lg p-6">
                            <h3 className="text-xl font-bold text-gray-800 mb-4">Project Success Rate</h3>
                            <div id="projectSuccessChart" className="w-full h-80"></div>
                        </div>
                    </div>
                </div>
            </section>
            {/* Team Expertise Showcase */}
            <section id="team" className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Meet Our Expert Team</h2>
                        <div className="w-24 h-1 bg-amber-600 mx-auto mb-6"></div>
                        <p className="text-gray-600 max-w-3xl mx-auto">
                            Our team of industry experts brings decades of combined experience to deliver exceptional results for your business.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:shadow-xl hover:-translate-y-2">
                            <div className="h-64 overflow-hidden relative group">
                                <img
                                    src="/isaacomollo.jpeg"
                                    alt="Isaac Omollo"
                                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-amber-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                                    <div className="p-4 w-full">
                                        <div className="flex justify-center space-x-4">
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-linkedin text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-twitter text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fas fa-envelope text-xl"></i>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-1">Isaac Omollo</h3>
                                <p className="text-amber-700 mb-3">Chief Executive Officer</p>
                                <p className="text-gray-600 mb-3">
                                    15+ years of experience in technology leadership and business transformation.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Strategy</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Leadership</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Innovation</span>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:shadow-xl hover:-translate-y-2">
                            <div className="h-64 overflow-hidden relative group">
                                <img
                                    src="/azronbrian.png"
                                    alt="Azron Brian"
                                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-amber-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                                    <div className="p-4 w-full">
                                        <div className="flex justify-center space-x-4">
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-linkedin text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-twitter text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fas fa-envelope text-xl"></i>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-1">Azron Brian</h3>
                                <p className="text-amber-700 mb-3">Chief Technology Officer</p>
                                <p className="text-gray-600 mb-3">
                                    Expert in enterprise architecture and emerging technologies with 12+ years experience.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Architecture</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Cloud</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">AI/ML</span>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:shadow-xl hover:-translate-y-2">
                            <div className="h-64 overflow-hidden relative group">
                                <img
                                    src="/hezbornshikuku.jpeg"
                                    alt="Hezborn Achido Shikuku"
                                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-amber-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                                    <div className="p-4 w-full">
                                        <div className="flex justify-center space-x-4">
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-linkedin text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-github text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fas fa-envelope text-xl"></i>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-1">Hezborn Achido Shikuku</h3>
                                <p className="text-amber-700 mb-3">Full Stack developer</p>
                                <p className="text-gray-600 mb-3">
                                    Specialized in designing,developing and maintaining scalable enterprise solutions and service based systems.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Javascript</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Go</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">DevOps</span>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:shadow-xl hover:-translate-y-2">
                            <div className="h-64 overflow-hidden relative group">
                                <img
                                    src="/victormaina.jpeg"
                                    alt="Victor Maina"
                                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-amber-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                                    <div className="p-4 w-full">
                                        <div className="flex justify-center space-x-4">
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-linkedin text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-twitter text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fas fa-envelope text-xl"></i>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-1">Victor Maina</h3>
                                <p className="text-amber-700 mb-3">Full Stack developer</p>
                                <p className="text-gray-600 mb-3">
                                    Certified PMP with expertise in agile methodologies and enterprise projects.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Agile</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">PMP</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Scrum</span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:shadow-xl hover:-translate-y-2">
                            <div className="h-64 overflow-hidden relative group">
                                <img
                                    src="/josh.png"
                                    alt="Joshua Odero"
                                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-amber-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                                    <div className="p-4 w-full">
                                        <div className="flex justify-center space-x-4">
                                            <a href="https://www.linkedin.com/in/joshua-ogallo-9a2628251/"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-linkedin text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-dribbble text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fas fa-envelope text-xl"></i>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-1">Joshua Odero</h3>
                                <p className="text-amber-700 mb-3">Creative Technologist</p>
                                <p className="text-gray-600 mb-3">
                                    Where design, engineering and creativity meets!
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Frontend Engineer</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Digital Marketer</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Creative designer</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:shadow-xl hover:-translate-y-2">
                            <div className="h-64 overflow-hidden relative group">
                                <img
                                    src="/mouriceadero.jpeg"
                                    alt="Mourice Adero"
                                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-amber-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                                    <div className="p-4 w-full">
                                        <div className="flex justify-center space-x-4">
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-linkedin text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-github text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fas fa-envelope text-xl"></i>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-1">Mourice Adero</h3>
                                <p className="text-amber-700 mb-3">Full stack Developer</p>
                                <p className="text-gray-600 mb-3">
                                    Expert in creating responsive and accessible web applications with modern frameworks.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">React</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Vue</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Tailwind</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:shadow-xl hover:-translate-y-2">
                            <div className="h-64 overflow-hidden relative group">
                                <img
                                    src="/ericodhiambo.jpeg"
                                    alt="Eric Odhiambo"
                                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-amber-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                                    <div className="p-4 w-full">
                                        <div className="flex justify-center space-x-4">
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-linkedin text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-github text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fas fa-envelope text-xl"></i>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="p-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-1">Eric Odhiambo</h3>
                                <p className="text-amber-700 mb-3">Business Analyst</p>
                                <p className="text-gray-600 mb-3">
                                    Specialized in translating business requirements into technical specifications.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Requirements</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Analysis</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Documentation</span>
                                </div>
                            </div>
                        </div>

                        {/* Other team profiles kept for reference
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:shadow-xl hover:-translate-y-2">
                            <div className="h-64 overflow-hidden relative group">
                                <img
                                    src="/joshua.png"
                                    alt="Joshua Ogallo"
                                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-amber-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                                    <div className="p-4 w-full">
                                        <div className="flex justify-center space-x-4">
                                            <a href="https://www.linkedin.com/in/joshua-ogallo-9a2628251/"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-linkedin text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-github text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fas fa-envelope text-xl"></i>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-1">Joshua Ogallo</h3>
                                <p className="text-amber-700 mb-3">Frontend Developer</p>
                                <p className="text-gray-600 mb-3">
                                    Specialized in building robust APIs and high-performance database systems.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">React</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Spring</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">SQL</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:shadow-xl hover:-translate-y-2">
                            <div className="h-64 overflow-hidden relative group">
                                <img
                                    src="/briansimiyu.jpg"
                                    alt="Brian Simiyu"
                                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-amber-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                                    <div className="p-4 w-full">
                                        <div className="flex justify-center space-x-4">
                                            <a href="https://www.linkedin.com/in/brian-brisn57?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app"
                                                className="text-white hover:text-amber-300 transition-colors cursor-pointer"
                                                target="_blank"
                                                rel="noopener noreferrer">
                                                <i className="fab fa-linkedin text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-twitter text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fas fa-envelope text-xl"></i>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-1">Brian Simiyu</h3>
                                <p className="text-amber-700 mb-3">QA Director</p>
                                <p className="text-gray-600 mb-3">
                                    ISTQB-certified expert in quality assurance methodologies and test automation.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Selenium</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">ISTQB</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Automation</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:shadow-xl hover:-translate-y-2">
                            <div className="h-64 overflow-hidden relative group">
                                <img
                                    src="/female.png"
                                    alt="Esther Njeri"
                                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-amber-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                                    <div className="p-4 w-full">
                                        <div className="flex justify-center space-x-4">
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-linkedin text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-github text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fas fa-envelope text-xl"></i>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-1">Esther Njeri</h3>
                                <p className="text-amber-700 mb-3">Mobile Development Lead</p>
                                <p className="text-gray-600 mb-3">
                                    Specialized in cross-platform mobile app development with a focus on performance.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">React Native</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Flutter</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">iOS/Android</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:shadow-xl hover:-translate-y-2">
                            <div className="h-64 overflow-hidden relative group">
                                <img
                                    src="/female.png"
                                    alt="Brigit Tarus"
                                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-amber-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                                    <div className="p-4 w-full">
                                        <div className="flex justify-center space-x-4">
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-linkedin text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-twitter text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fas fa-envelope text-xl"></i>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="p-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-1">Eric Odhiambo</h3>
                                <p className="text-amber-700 mb-3">Lead data scientist</p>
                                <p className="text-gray-600 mb-3">
                                    Expert in machine learning and predictive analytics with a focus on business applications.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">ML</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Python</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">TensorFlow</span>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:shadow-xl hover:-translate-y-2">
                            <div className="h-64 overflow-hidden relative group">
                                <img
                                    src="/male.png"
                                    alt="Almas Angwenyi"
                                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-amber-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                                    <div className="p-4 w-full">
                                        <div className="flex justify-center space-x-4">
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-linkedin text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-twitter text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fas fa-envelope text-xl"></i>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-1">Almas Angwenyi</h3>
                                <p className="text-amber-700 mb-3">Cybersecurity Director</p>
                                <p className="text-gray-600 mb-3">
                                    CISSP-certified expert specializing in enterprise security architecture and compliance.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Security</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">CISSP</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Compliance</span>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:shadow-xl hover:-translate-y-2">
                            <div className="h-64 overflow-hidden relative group">
                                <img
                                    src="/male.png"
                                    alt="Azron Brian"
                                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-amber-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                                    <div className="p-4 w-full">
                                        <div className="flex justify-center space-x-4">
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-linkedin text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-github text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fas fa-envelope text-xl"></i>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-1">Azron Brian</h3>
                                <p className="text-amber-700 mb-3">DevOps Lead</p>
                                <p className="text-gray-600 mb-3">
                                    Expert in CI/CD pipelines and infrastructure automation for enterprise applications.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Kubernetes</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Docker</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Jenkins</span>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:shadow-xl hover:-translate-y-2">
                            <div className="h-64 overflow-hidden relative group">
                                <img
                                    src="/female.png"
                                    alt="Brigit tarus"
                                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-amber-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                                    <div className="p-4 w-full">
                                        <div className="flex justify-center space-x-4">
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-linkedin text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fab fa-twitter text-xl"></i>
                                            </a>
                                            <a href="#" className="text-white hover:text-amber-300 transition-colors cursor-pointer">
                                                <i className="fas fa-envelope text-xl"></i>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="p-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-1">Linda Atieno</h3>
                                <p className="text-amber-700 mb-3">Product Manager</p>
                                <p className="text-gray-600 mb-3">
                                    Experienced in product strategy, roadmap development, and market analysis.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Strategy</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Roadmapping</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">User Research</span>
                                </div>
                            </div>
                        </div>
                        */}
                    </div>
                </div>
            </section>
            {/* Testimonials */}
            <section className="py-20 bg-gray-900 text-white">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">What Our Clients Say</h2>
                        <div className="w-24 h-1 bg-amber-500 mx-auto mb-6"></div>
                        <p className="text-gray-300 max-w-3xl mx-auto">
                            Hear from organizations that have transformed their operations with our technology solutions.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="bg-gray-800 rounded-lg p-8 relative">
                            <div className="text-amber-500 absolute -top-5 left-8 text-5xl opacity-50">
                                <i className="fas fa-quote-left"></i>
                            </div>
                            <p className="text-gray-300 mb-6 relative z-10">
                                "BaruchSoft transformed our project management processes, resulting in a 40% increase in team productivity and significantly improved client satisfaction. Their attention to detail and commitment to excellence is unmatched."
                            </p>
                            <div className="flex items-center">
                                <div className="w-12 h-12 rounded-full overflow-hidden mr-4">
                                    <img
                                        src="https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20an%20African%20business%20executive%20in%20formal%20attire%20against%20a%20neutral%20background%20with%20professional%20lighting&width=100&height=100&seq=10&orientation=squarish"
                                        alt="Henry Odero"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div>
                                    <h4 className="font-bold">Henry Odero</h4>
                                    <p className="text-amber-500 text-sm">CEO, Hillax Investment</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-8 relative">
                            <div className="text-amber-500 absolute -top-5 left-8 text-5xl opacity-50">
                                <i className="fas fa-quote-left"></i>
                            </div>
                            <p className="text-gray-300 mb-6 relative z-10">
                                "The custom software solution developed by BaruchSoft has revolutionized our operations. Their team's expertise and dedication to understanding our unique needs resulted in a system that perfectly aligns with our business objectives."
                            </p>
                            <div className="flex items-center">
                                <div className="w-12 h-12 rounded-full overflow-hidden mr-4">
                                    <img
                                        src="/nancynguru.png"
                                        alt="Nancy Nguru"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div>
                                    <h4 className="font-bold">Nancy Nguru</h4>
                                    <p className="text-amber-500 text-sm">Founder T&S Solutions</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-8 relative">
                            <div className="text-amber-500 absolute -top-5 left-8 text-5xl opacity-50">
                                <i className="fas fa-quote-left"></i>
                            </div>
                            <p className="text-gray-300 mb-6 relative z-10">
                                "BaruchSoft's IT consulting services provided us with the strategic guidance we needed to make informed technology decisions. Their expertise has been instrumental in our digital transformation journey."
                            </p>
                            <div className="flex items-center">
                                <div className="w-12 h-12 rounded-full overflow-hidden mr-4">
                                    <img
                                        src="/mouriceolunga.png"
                                        alt="Mourice Olunga"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div>
                                    <h4 className="font-bold">Mourice Olunga</h4>
                                    <p className="text-amber-500 text-sm">CTO, Uhai Africa</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            {/* Resource Center */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Resource Center</h2>
                        <div className="w-24 h-1 bg-amber-600 mx-auto mb-6"></div>
                        <p className="text-gray-600 max-w-3xl mx-auto">
                            Access our comprehensive collection of industry insights, technical guides, and expert knowledge to help drive your business forward.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                            <div className="h-48 overflow-hidden">
                                <img
                                    src="https://readdy.ai/api/search-image?query=Professional%20knowledge%20base%20interface%20showing%20elegant%20documentation%20and%20technical%20guides%20with%20modern%20design%20elements.%20The%20scene%20features%20clean%20typography%2C%20organized%20content%20sections%2C%20and%20subtle%20gold%20accents%20against%20a%20neutral%20sophisticated%20background&width=600&height=400&seq=13&orientation=landscape"
                                    alt="Knowledge Base"
                                    className="w-full h-full object-cover object-top"
                                />
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-3">Knowledge Base</h3>
                                <p className="text-gray-600 mb-4">
                                    Comprehensive guides and documentation to help you maximize our solutions.
                                </p>
                                <button className="text-amber-700 hover:text-amber-800 font-medium flex items-center !rounded-button whitespace-nowrap cursor-pointer">
                                    Browse Articles <i className="fas fa-arrow-right ml-2"></i>
                                </button>
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                            <div className="h-48 overflow-hidden">
                                <img
                                    src="https://readdy.ai/api/search-image?query=Professional%20video%20tutorial%20interface%20showing%20high%20quality%20technical%20training%20content.%20The%20scene%20features%20modern%20video%20player%20controls%2C%20course%20progression%20elements%2C%20and%20elegant%20design%20with%20subtle%20gold%20accents%20against%20a%20neutral%20sophisticated%20background&width=600&height=400&seq=14&orientation=landscape"
                                    alt="Video Tutorials"
                                    className="w-full h-full object-cover object-top"
                                />
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-3">Video Tutorials</h3>
                                <p className="text-gray-600 mb-4">
                                    Step-by-step video guides to help you get the most out of our platform.
                                </p>
                                <button className="text-amber-700 hover:text-amber-800 font-medium flex items-center !rounded-button whitespace-nowrap cursor-pointer">
                                    Watch Now <i className="fas fa-play ml-2"></i>
                                </button>
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                            <div className="h-48 overflow-hidden">
                                <img
                                    src="https://readdy.ai/api/search-image?query=Professional%20blog%20interface%20showing%20premium%20technical%20articles%20and%20industry%20insights.%20The%20scene%20features%20elegant%20typography%2C%20organized%20content%20layout%2C%20and%20modern%20design%20elements%20with%20subtle%20gold%20accents%20against%20a%20neutral%20sophisticated%20background&width=600&height=400&seq=15&orientation=landscape"
                                    alt="Blog & Insights"
                                    className="w-full h-full object-cover object-top"
                                />
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-3">Blog & Insights</h3>
                                <p className="text-gray-600 mb-4">
                                    Latest industry trends, technical articles, and company updates.
                                </p>
                                <button className="text-amber-700 hover:text-amber-800 font-medium flex items-center !rounded-button whitespace-nowrap cursor-pointer">
                                    Read More <i className="fas fa-book-reader ml-2"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            {/* Case Studies & Portfolio */}
            <section id="portfolio" className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Success Stories & Portfolio</h2>
                        <div className="w-24 h-1 bg-amber-600 mx-auto mb-6"></div>
                        <p className="text-gray-600 max-w-3xl mx-auto text-sm sm:text-base">
                            Explore our production-grade systems across insurance, payments, enterprise ERP/HR, retail, and healthcare.
                        </p>

                        {/* Filter Tabs */}
                        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mt-8">
                            {filterCategories.map(cat => (
                                <button
                                    key={cat.key}
                                    onClick={() => setPortfolioFilter(cat.key)}
                                    className={`px-4 py-2 rounded-full text-xs sm:text-sm font-semibold transition-all cursor-pointer !rounded-button ${
                                        portfolioFilter === cat.key
                                            ? 'bg-amber-600 text-white shadow-md'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    {cat.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {filteredPortfolio.map(item => (
                            <div key={item.id} className="bg-white rounded-lg shadow-lg overflow-hidden flex flex-col justify-between border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                                <div className="h-60 overflow-hidden relative">
                                    <img
                                        src={item.image}
                                        alt={item.title}
                                        className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
                                        loading="lazy"
                                    />
                                    <div className="absolute top-3 right-3 bg-gray-900/80 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-full font-medium">
                                        {item.duration}
                                    </div>
                                </div>
                                <div className="p-6 flex-1 flex flex-col justify-between">
                                    <div>
                                        <div className="flex items-center mb-3">
                                            <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full font-medium">
                                                {item.categoryTag}
                                            </span>
                                            <span className="mx-2 text-gray-400">•</span>
                                            <span className="text-gray-500 text-xs font-medium">{item.category}</span>
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-800 mb-2">{item.title}</h3>
                                        <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                                            {item.summary}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setSelectedCaseStudy(item)}
                                        className="text-amber-700 hover:text-amber-800 font-semibold flex items-center !rounded-button whitespace-nowrap cursor-pointer text-sm mt-2"
                                    >
                                        View Case Study <i className="fas fa-arrow-right ml-2"></i>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            {/* Technology Stack */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Our Technology Stack</h2>
                        <div className="w-24 h-1 bg-amber-600 mx-auto mb-6"></div>
                        <p className="text-gray-600 max-w-3xl mx-auto">
                            We leverage cutting-edge technologies to deliver scalable and efficient solutions for our clients.
                        </p>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        <div className="bg-white p-6 rounded-lg shadow-md text-center">
                            <div className="text-4xl text-amber-700 mb-4">
                                <i className="fab fa-aws"></i>
                            </div>
                            <h3 className="font-bold text-gray-800 mb-2">AWS</h3>
                            <p className="text-gray-600 text-sm">Cloud Infrastructure</p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md text-center">
                            <div className="text-4xl text-amber-700 mb-4">
                                <i className="fab fa-angular"></i>
                            </div>
                            <h3 className="font-bold text-gray-800 mb-2">Angular</h3>
                            <p className="text-gray-600 text-sm">Frontend Framework</p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md text-center">
                            <div className="text-4xl text-amber-700 mb-4">
                                <i className="fab fa-vuejs"></i>
                            </div>
                            <h3 className="font-bold text-gray-800 mb-2">Vue.js</h3>
                            <p className="text-gray-600 text-sm">Frontend Framework</p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md text-center">
                            <div className="text-4xl text-amber-700 mb-4">
                                <i className="fab fa-laravel"></i>
                            </div>
                            <h3 className="font-bold text-gray-800 mb-2">Laravel</h3>
                            <p className="text-gray-600 text-sm">Backend Framework</p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md text-center">
                            <div className="text-4xl text-amber-700 mb-4">
                                <i className="fab fa-react"></i>
                            </div>
                            <h3 className="font-bold text-gray-800 mb-2">React</h3>
                            <p className="text-gray-600 text-sm">Frontend Development</p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md text-center">
                            <div className="text-4xl text-amber-700 mb-4">
                                <i className="fab fa-node-js"></i>
                            </div>
                            <h3 className="font-bold text-gray-800 mb-2">Node.js</h3>
                            <p className="text-gray-600 text-sm">Backend Development</p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md text-center">
                            <div className="text-4xl text-amber-700 mb-4">
                                <i className="fas fa-database"></i>
                            </div>
                            <h3 className="font-bold text-gray-800 mb-2">MongoDB</h3>
                            <p className="text-gray-600 text-sm">Database</p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md text-center">
                            <div className="text-4xl text-amber-700 mb-4">
                                <i className="fab fa-python"></i>
                            </div>
                            <h3 className="font-bold text-gray-800 mb-2">Python</h3>
                            <p className="text-gray-600 text-sm">Data Processing</p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md text-center">
                            <div className="text-4xl text-amber-700 mb-4">
                                <i className="fab fa-docker"></i>
                            </div>
                            <h3 className="font-bold text-gray-800 mb-2">Docker</h3>
                            <p className="text-gray-600 text-sm">Containerization</p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md text-center">
                            <div className="text-4xl text-amber-700 mb-4">
                                <i className="fas fa-shield-alt"></i>
                            </div>
                            <h3 className="font-bold text-gray-800 mb-2">Security</h3>
                            <p className="text-gray-600 text-sm">Enterprise Grade</p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md text-center">
                            <div className="text-4xl text-amber-700 mb-4">
                                <i className="fas fa-mobile-alt"></i>
                            </div>
                            <h3 className="font-bold text-gray-800 mb-2">Mobile</h3>
                            <p className="text-gray-600 text-sm">Cross-platform Apps</p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md text-center">
                            <div className="text-4xl text-amber-700 mb-4">
                                <i className="fab fa-java"></i>
                            </div>
                            <h3 className="font-bold text-gray-800 mb-2">Java</h3>
                            <p className="text-gray-600 text-sm">Backend -Microservices</p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md text-center">
                            <div className="text-4xl text-amber-700 mb-4">
                                <i className="fas fa-network-wired"></i>
                            </div>
                            <h3 className="font-bold text-gray-800 mb-2">WSO2</h3>
                            <p className="text-gray-600 text-sm">API Gateway & ESB</p>
                        </div>
                    </div>
                    <div className="text-center mt-12">
                        <button className="bg-amber-700 hover:bg-amber-800 text-white px-8 py-3 rounded-md shadow-md transition-colors font-medium !rounded-button whitespace-nowrap cursor-pointer">
                            Explore Our Solutions
                        </button>
                    </div>
                </div>
            </section>
            {/* Innovation Lab Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Innovation Lab</h2>
                        <div className="w-24 h-1 bg-amber-600 mx-auto mb-6"></div>
                        <p className="text-gray-600 max-w-3xl mx-auto">
                            Explore our latest research and development initiatives that are shaping the future of technology solutions.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                            <div className="h-48 overflow-hidden">
                                <img
                                    src="https://readdy.ai/api/search-image?query=Advanced%20artificial%20intelligence%20visualization%20showing%20neural%20networks%20and%20machine%20learning%20algorithms%20in%20action.%20Modern%20research%20lab%20environment%20with%20sophisticated%20technology%20displays%20and%20elegant%20design%20elements%20against%20a%20neutral%20background&width=600&height=400&seq=19&orientation=landscape"
                                    alt="AI Research"
                                    className="w-full h-full object-cover object-top"
                                />
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-3">AI & Machine Learning</h3>
                                <p className="text-gray-600 mb-4">
                                    Cutting-edge research in artificial intelligence and machine learning applications.
                                </p>
                                <div className="flex flex-wrap gap-2 mb-4">
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Neural Networks</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Deep Learning</span>
                                </div>
                                <button className="text-amber-700 hover:text-amber-800 font-medium flex items-center !rounded-button whitespace-nowrap cursor-pointer">
                                    Learn More <i className="fas fa-arrow-right ml-2"></i>
                                </button>
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                            <div className="h-48 overflow-hidden">
                                <img
                                    src="https://readdy.ai/api/search-image?query=Blockchain%20technology%20visualization%20showing%20secure%20distributed%20ledger%20system%20and%20smart%20contracts.%20Modern%20tech%20environment%20with%20sophisticated%20displays%20and%20elegant%20design%20elements%20against%20a%20neutral%20background&width=600&height=400&seq=20&orientation=landscape"
                                    alt="Blockchain Research"
                                    className="w-full h-full object-cover object-top"
                                />
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-3">Blockchain Solutions</h3>
                                <p className="text-gray-600 mb-4">
                                    Revolutionary blockchain applications for secure and transparent transactions.
                                </p>
                                <div className="flex flex-wrap gap-2 mb-4">
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Smart Contracts</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">DeFi</span>
                                </div>
                                <button className="text-amber-700 hover:text-amber-800 font-medium flex items-center !rounded-button whitespace-nowrap cursor-pointer">
                                    Learn More <i className="fas fa-arrow-right ml-2"></i>
                                </button>
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                            <div className="h-48 overflow-hidden">
                                <img
                                    src="https://readdy.ai/api/search-image?query=Internet%20of%20Things%20ecosystem%20visualization%20showing%20connected%20devices%20and%20smart%20systems.%20Modern%20tech%20environment%20with%20sophisticated%20sensor%20networks%20and%20elegant%20design%20elements%20against%20a%20neutral%20background&width=600&height=400&seq=21&orientation=landscape"
                                    alt="IoT Research"
                                    className="w-full h-full object-cover object-top"
                                />
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-3">IoT Innovations</h3>
                                <p className="text-gray-600 mb-4">
                                    Next-generation Internet of Things solutions for smart environments.
                                </p>
                                <div className="flex flex-wrap gap-2 mb-4">
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Smart Devices</span>
                                    <span className="bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full">Edge Computing</span>
                                </div>
                                <button className="text-amber-700 hover:text-amber-800 font-medium flex items-center !rounded-button whitespace-nowrap cursor-pointer">
                                    Learn More <i className="fas fa-arrow-right ml-2"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            {/* Quality Assurance Section */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Quality Assurance</h2>
                        <div className="w-24 h-1 bg-amber-600 mx-auto mb-6"></div>
                        <p className="text-gray-600 max-w-3xl mx-auto">
                            Our comprehensive quality assurance process ensures the delivery of robust and reliable solutions.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                        <div className="space-y-8">
                            <div className="flex items-start">
                                <div className="bg-amber-100 rounded-full p-3 mr-4">
                                    <i className="fas fa-code-branch text-amber-700 text-xl"></i>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-800 mb-2">Automated Testing</h3>
                                    <p className="text-gray-600">
                                        Comprehensive automated testing suite ensuring code quality and functionality across all development stages.
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-start">
                                <div className="bg-amber-100 rounded-full p-3 mr-4">
                                    <i className="fas fa-shield-alt text-amber-700 text-xl"></i>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-800 mb-2">Security Testing</h3>
                                    <p className="text-gray-600">
                                        Rigorous security assessments and penetration testing to protect against vulnerabilities.
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-start">
                                <div className="bg-amber-100 rounded-full p-3 mr-4">
                                    <i className="fas fa-tachometer-alt text-amber-700 text-xl"></i>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-800 mb-2">Performance Testing</h3>
                                    <p className="text-gray-600">
                                        Comprehensive performance analysis ensuring optimal speed and reliability under load.
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-start">
                                <div className="bg-amber-100 rounded-full p-3 mr-4">
                                    <i className="fas fa-mobile-alt text-amber-700 text-xl"></i>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-800 mb-2">Cross-platform Testing</h3>
                                    <p className="text-gray-600">
                                        Thorough testing across multiple devices and platforms to ensure consistent functionality.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="relative">
                            <img
                                src="https://readdy.ai/api/search-image?query=Quality%20assurance%20and%20testing%20process%20visualization%20showing%20modern%20testing%20environment%20with%20multiple%20screens%20displaying%20test%20results%2C%20code%20coverage%2C%20and%20performance%20metrics.%20Professional%20tech%20setting%20with%20elegant%20design%20elements%20against%20a%20neutral%20background&width=800&height=600&seq=22&orientation=landscape"
                                alt="Quality Assurance Process"
                                className="rounded-lg shadow-xl w-full h-full object-cover"
                            />
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-amber-900/80 to-transparent p-6">
                                <div className="text-white">
                                    <h3 className="text-xl font-bold mb-2">Our Quality Standards</h3>
                                    <ul className="space-y-2">
                                        <li className="flex items-center">
                                            <i className="fas fa-check-circle mr-2 text-amber-400"></i>
                                            ISO 9001:2015 Certified
                                        </li>
                                        <li className="flex items-center">
                                            <i className="fas fa-check-circle mr-2 text-amber-400"></i>
                                            CMMI Level 5
                                        </li>
                                        <li className="flex items-center">
                                            <i className="fas fa-check-circle mr-2 text-amber-400"></i>
                                            99.9% Code Coverage
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            {/* Contact Section */}
            <section id="contact" className="py-12 sm:py-16 md:py-20 bg-white">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="text-center mb-10 sm:mb-12 md:hidden">
                        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-3">Get in Touch</h2>
                        <div className="w-16 sm:w-24 h-1 bg-amber-600 mx-auto mb-4"></div>
                    </div>
                    <div className="flex flex-col md:flex-row">
                        <div className="md:w-1/2 mb-8 md:mb-0 md:pr-12">
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 sm:mb-6 hidden md:block">Get in Touch</h2>
                            <p className="text-gray-600 mb-6 sm:mb-8 text-sm sm:text-base">
                                Ready to transform your business with our premium technology solutions? Contact us today to schedule a consultation with our experts.
                            </p>
                            <div className="space-y-5 sm:space-y-6">
                                <div className="flex items-start">
                                    <div className="text-amber-600 mr-4 mt-1 bg-amber-50 p-2 rounded-full">
                                        <i className="fas fa-map-marker-alt text-lg sm:text-xl"></i>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-800 mb-1">Visit Us</h3>
                                        <p className="text-gray-600 text-sm sm:text-base">
                                            BRICK HOUSE, ADAM ARCADE, NAIROBI<br />
                                            STAREHE DISTRICT CBD<br />
                                            P.O BOX 24483, 00100 - G.P.O NAIROBI
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="text-amber-600 mr-4 mt-1 bg-amber-50 p-2 rounded-full">
                                        <i className="fas fa-phone-alt text-lg sm:text-xl"></i>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-800 mb-1">Call Us</h3>
                                        <p className="text-gray-600 text-sm sm:text-base">
                                            <a href="tel:+254723456789" className="hover:text-amber-700">+254 707 279936</a>
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="text-amber-600 mr-4 mt-1 bg-amber-50 p-2 rounded-full">
                                        <i className="fas fa-envelope text-lg sm:text-xl"></i>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-800 mb-1">Email Us</h3>
                                        <p className="text-gray-600 text-sm sm:text-base">
                                            <a href="mailto:info@baruchsoft.com" className="hover:text-amber-700">iomollo@baruchsoft.co.ke</a>
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="text-amber-600 mr-4 mt-1 bg-amber-50 p-2 rounded-full">
                                        <i className="fas fa-clock text-lg sm:text-xl"></i>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-800 mb-1">Business Hours</h3>
                                        <p className="text-gray-600 text-sm sm:text-base">Monday - Friday: 8:00 AM - 6:00 PM</p>
                                        <p className="text-gray-600 text-sm sm:text-base">Saturday: 9:00 AM - 1:00 PM</p>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-8 flex space-x-5">
                                <a href="https://www.linkedin.com/company/baruchsoft-technologies/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-gray-800 hover:bg-amber-700 text-white w-12 h-12 rounded-full flex items-center justify-center transition-colors cursor-pointer shadow-md">
                                    <i className="fab fa-linkedin-in text-lg"></i>
                                </a>
                                <a href="#" className="bg-gray-800 hover:bg-amber-700 text-white w-12 h-12 rounded-full flex items-center justify-center transition-colors cursor-pointer shadow-md">
                                    <i className="fab fa-twitter text-lg"></i>
                                </a>
                                <a href="#" className="bg-gray-800 hover:bg-amber-700 text-white w-12 h-12 rounded-full flex items-center justify-center transition-colors cursor-pointer shadow-md">
                                    <i className="fab fa-facebook-f text-lg"></i>
                                </a>
                                <a href="#" className="bg-gray-800 hover:bg-amber-700 text-white w-12 h-12 rounded-full flex items-center justify-center transition-colors cursor-pointer shadow-md">
                                    <i className="fab fa-instagram text-lg"></i>
                                </a>
                            </div>
                        </div>
                        <div className="md:w-1/2">
                            <div className="bg-gray-50 rounded-lg shadow-lg p-5 sm:p-8">
                                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">Send Us a Message</h3>
                                {contactSubmitted ? (
                                    <div className="bg-emerald-50 border border-emerald-300 text-emerald-800 p-6 rounded-lg text-center">
                                        <i className="fas fa-check-circle text-3xl text-emerald-600 mb-2"></i>
                                        <h4 className="text-lg font-bold">Message Received!</h4>
                                        <p className="text-sm mt-1">Thank you for reaching out to BaruchSoft. Our team will contact you shortly.</p>
                                        <button
                                            type="button"
                                            onClick={() => setContactSubmitted(false)}
                                            className="mt-4 inline-block text-xs font-semibold text-emerald-700 underline hover:text-emerald-900 cursor-pointer"
                                        >
                                            Send another message
                                        </button>
                                    </div>
                                ) : (
                                    <form onSubmit={(e) => { e.preventDefault(); setContactSubmitted(true); }}>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                                            <div>
                                                <label htmlFor="name" className="block text-gray-700 mb-2 text-sm sm:text-base">Full Name</label>
                                                <input
                                                    type="text"
                                                    id="name"
                                                    required
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-base"
                                                    placeholder="Your name"
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="email" className="block text-gray-700 mb-2 text-sm sm:text-base">Email Address</label>
                                                <input
                                                    type="email"
                                                    id="email"
                                                    required
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-base"
                                                    placeholder="Your email"
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-4 sm:mb-6">
                                            <label htmlFor="subject" className="block text-gray-700 mb-2 text-sm sm:text-base">Subject</label>
                                            <input
                                                type="text"
                                                id="subject"
                                                required
                                                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-base"
                                                placeholder="Subject"
                                            />
                                        </div>
                                        <div className="mb-4 sm:mb-6">
                                            <label htmlFor="message" className="block text-gray-700 mb-2 text-sm sm:text-base">Message</label>
                                            <textarea
                                                id="message"
                                                rows={4}
                                                required
                                                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-base"
                                                placeholder="Your message"
                                            ></textarea>
                                        </div>
                                        <button
                                            type="submit"
                                            className="bg-amber-700 hover:bg-amber-800 text-white px-6 py-3 rounded-md shadow-md transition-colors font-medium w-full !rounded-button whitespace-nowrap cursor-pointer flex items-center justify-center"
                                        >
                                            <i className="fas fa-paper-plane mr-2"></i>
                                            <span>Send Message</span>
                                        </button>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            {/* Footer */}
            <footer className="bg-gray-950 text-white pt-16 pb-8 border-t border-gray-900">
                <div className="container mx-auto px-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
                        <div>
                            <div className="flex items-center mb-6">
                                <img src="/logo.png" alt="BaruchSoft Logo" className="h-14 w-auto object-contain brightness-0 invert rounded-xl border border-white/20 p-1 bg-white/5" />
                            </div>
                            <p className="text-gray-400 mb-6">
                                Premium software development and IT consulting services tailored to transform your business operations.
                            </p>
                            <div className="flex space-x-4">
                                <a href="#" className="text-gray-400 hover:text-amber-400 transition-colors cursor-pointer">
                                    <i className="fab fa-linkedin-in"></i>
                                </a>
                                <a href="#" className="text-gray-400 hover:text-amber-400 transition-colors cursor-pointer">
                                    <i className="fab fa-twitter"></i>
                                </a>
                                <a href="#" className="text-gray-400 hover:text-amber-400 transition-colors cursor-pointer">
                                    <i className="fab fa-facebook-f"></i>
                                </a>
                                <a href="#" className="text-gray-400 hover:text-amber-400 transition-colors cursor-pointer">
                                    <i className="fab fa-instagram"></i>
                                </a>
                            </div>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold mb-6 text-amber-400">Services</h3>
                            <ul className="space-y-3">
                                <li>
                                    <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                                        WSO2 API Management & Integration
                                    </a>
                                </li>
                                <li>
                                    <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                                        Project Management Solutions
                                    </a>
                                </li>
                                <li>
                                    <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                                        Custom Software Development
                                    </a>
                                </li>
                                <li>
                                    <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                                        IT Consulting Services
                                    </a>
                                </li>
                                <li>
                                    <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                                        Enterprise Solutions
                                    </a>
                                </li>
                                <li>
                                    <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                                        Mobile App Development
                                    </a>
                                </li>
                                <li>
                                    <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                                        Cloud Services
                                    </a>
                                </li>
                            </ul>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold mb-6 text-amber-400">Company</h3>
                            <ul className="space-y-3">
                                <li>
                                    <a href="#home" onClick={(e) => { e.preventDefault(); scrollToSection('home'); }} className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                                        About Us
                                    </a>
                                </li>
                                <li>
                                    <a href="#team" onClick={(e) => { e.preventDefault(); scrollToSection('team'); }} className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                                        Our Team
                                    </a>
                                </li>
                                <li>
                                    <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                                        Careers
                                    </a>
                                </li>
                                <li>
                                    <a href="#portfolio" onClick={(e) => { e.preventDefault(); scrollToSection('portfolio'); }} className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                                        Case Studies
                                    </a>
                                </li>
                                <li>
                                    <a href="#portfolio" onClick={(e) => { e.preventDefault(); scrollToSection('portfolio'); }} className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                                        Blog
                                    </a>
                                </li>
                                <li>
                                    <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                                        Contact Us
                                    </a>
                                </li>
                            </ul>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold mb-6 text-amber-400">Newsletter</h3>
                            <p className="text-gray-400 mb-4">
                                Subscribe to our newsletter to receive updates on our latest services and industry insights.
                            </p>
                            {newsletterSubmitted ? (
                                <div className="bg-emerald-900/40 border border-emerald-500/50 text-emerald-200 text-xs px-3 py-2.5 rounded-md mb-4 flex items-center">
                                    <i className="fas fa-check-circle text-emerald-400 mr-2 text-sm"></i>
                                    <span>Thank you for subscribing!</span>
                                </div>
                            ) : (
                                <form className="mb-4" onSubmit={(e) => { e.preventDefault(); setNewsletterSubmitted(true); }}>
                                    <div className="flex">
                                        <input
                                            type="email"
                                            required
                                            placeholder="Your email address"
                                            className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-l-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-white"
                                        />
                                        <button
                                            type="submit"
                                            className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-r-md transition-colors !rounded-button whitespace-nowrap cursor-pointer"
                                        >
                                            <i className="fas fa-paper-plane"></i>
                                        </button>
                                    </div>
                                </form>
                            )}
                            <div>
                                <h4 className="text-sm font-semibold mb-3 text-gray-300">We Accept</h4>
                                <div className="flex space-x-3">
                                    <i className="fab fa-cc-visa text-2xl text-gray-400"></i>
                                    <i className="fab fa-cc-mastercard text-2xl text-gray-400"></i>
                                    <i className="fab fa-cc-paypal text-2xl text-gray-400"></i>
                                    <i className="fab fa-cc-amex text-2xl text-gray-400"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 pt-8">
                        <div className="flex flex-col md:flex-row justify-between items-center">
                            <p className="text-gray-500 text-sm mb-4 md:mb-0">
                                &copy; {new Date().getFullYear()} BaruchSoft. All rights reserved.
                            </p>
                            <div className="flex space-x-6">
                                <a href="#" className="text-gray-500 hover:text-gray-300 text-sm transition-colors cursor-pointer">
                                    Privacy Policy
                                </a>
                                <a href="#" className="text-gray-500 hover:text-gray-300 text-sm transition-colors cursor-pointer">
                                    Terms of Service
                                </a>
                                <a href="#" className="text-gray-500 hover:text-gray-300 text-sm transition-colors cursor-pointer">
                                    Cookie Policy
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </footer>

            {/* Interactive Case Study & Service Modal */}
            {selectedCaseStudy && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in" onClick={() => setSelectedCaseStudy(null)}>
                    <div className="bg-gray-900 border border-gray-700/80 rounded-2xl max-w-2xl w-full p-6 sm:p-8 text-left shadow-2xl relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={() => setSelectedCaseStudy(null)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 w-9 h-9 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                            aria-label="Close modal"
                        >
                            <i className="fas fa-times text-lg"></i>
                        </button>
                        <div className="flex items-center space-x-3 mb-3">
                            <span className="bg-amber-500/20 text-amber-400 border border-amber-500/30 text-xs px-3 py-1 rounded-full font-medium">
                                {selectedCaseStudy.categoryTag || selectedCaseStudy.category}
                            </span>
                            <span className="text-gray-400 text-xs sm:text-sm">{selectedCaseStudy.duration}</span>
                        </div>
                        <h3 className="text-xl sm:text-2xl font-bold text-white mb-4">{selectedCaseStudy.title}</h3>
                        {selectedCaseStudy.image && (
                            <div className="h-52 w-full rounded-xl overflow-hidden mb-6 border border-gray-800">
                                <img src={selectedCaseStudy.image} alt={selectedCaseStudy.title} className="w-full h-full object-cover" />
                            </div>
                        )}
                        <p className="text-gray-300 leading-relaxed mb-6 text-sm sm:text-base">
                            {selectedCaseStudy.description}
                        </p>
                        {selectedCaseStudy.highlights && (
                            <div className="mb-6">
                                <h4 className="text-amber-400 font-semibold text-xs sm:text-sm uppercase tracking-wider mb-3">Key Features & Architectural Highlights</h4>
                                <ul className="space-y-2">
                                    {selectedCaseStudy.highlights.map((h, idx) => (
                                        <li key={idx} className="flex items-start text-xs sm:text-sm text-gray-300">
                                            <i className="fas fa-check-circle text-amber-500 mt-1 mr-2 flex-shrink-0"></i>
                                            <span>{h}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {selectedCaseStudy.impact && (
                            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6">
                                <div className="text-xs font-semibold uppercase tracking-wider text-amber-400 mb-1">Business Impact & Outcomes</div>
                                <p className="text-xs sm:text-sm text-amber-100 font-medium">{selectedCaseStudy.impact}</p>
                            </div>
                        )}
                        <div className="flex justify-end">
                            <button
                                onClick={() => setSelectedCaseStudy(null)}
                                className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2.5 rounded-lg font-medium transition-colors shadow-md !rounded-button cursor-pointer"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
export default App
